import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider } from "@mui/material";
import {
  Routes,
  Route,
  Outlet,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import NavBar from "./components/global/NavBar";
import Sidebar, {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from "./components/global/SideBar/Sidebar.jsx";
import Landing from "./components/global/Landing";
import { ThreadContext } from "./threadContext";
import { useState, useEffect, useCallback, Suspense } from "react";
import { UserContext } from "./UserContext";
import { ThreadsProvider } from "./pages/Threads/context/ThreadsContext";
import CheckSession from "./utils/CheckSession";
import { LicenseInfo } from "@mui/x-license-pro";
import axios from "axios";
import Swal from "sweetalert2";
import { colorTokens } from "./styles/designTokens";
import { publicRoutes, privateRoutes } from "./routes";

const MUI_LICENSE_KEY = process.env.REACT_APP_MUI_LICENSE_KEY;
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const PrivateRoutes = () => {
  const isLogged =
    !!sessionStorage?.getItem("jwt") || !!localStorage.getItem("jwt");

  return isLogged ? <Outlet /> : <Navigate to="/" />;
};

function App() {
  const [key, setKey] = useState(null);

  const [theme, colorMode] = useMode();
  const [thread, setThread] = useState(null);
  const [sidebarLayout, setSidebarLayout] = useState({
    width: SIDEBAR_COLLAPSED_WIDTH,
    isCollapsed: true,
  });
  const backgroundCanvas = colorTokens.backgroundSecondary || "#f5f7fb";
  const location = useLocation();
  const hideChrome = location.pathname === "/";
  const sidebarOffset = hideChrome ? 0 : sidebarLayout.width || 0;

  const navigate = useNavigate();

  let lsrole = localStorage.getItem("role");
  const handleSidebarStateChange = useCallback((state) => {
    if (!state) return;
    setSidebarLayout({
      width:
        typeof state.width === "number" ? state.width : SIDEBAR_EXPANDED_WIDTH,
      isCollapsed: Boolean(state.isCollapsed),
    });
  }, []);

  const checkToken = async () => {
    const id = localStorage.getItem("id");

    const userData = await axios.get(
      `${backendUrl}/api/users/${id}?populate=role`,
      {
        headers: {
          Authorization: `Bearer ${key}`,
        },
      }
    );

    if (userData.data.role.name !== lsrole) {
      sessionStorage.removeItem("jwt");
      localStorage.removeItem("jwt");
      localStorage.removeItem("username");
      localStorage.removeItem("email");
      localStorage.removeItem("id");
      localStorage.removeItem("session");
      localStorage.removeItem("role");
      Swal.fire(
        "Su sesión expiró, por favor ingresar nuevamente",
        "",
        "info"
      ).then(navigate(`/`, { replace: true }));
    }
  };

  useEffect(() => {
    const legacyJwt = localStorage.getItem("jwt");
    if (legacyJwt && !sessionStorage.getItem("jwt")) {
      sessionStorage.setItem("jwt", legacyJwt);
    }
    if (legacyJwt) {
      localStorage.removeItem("jwt");
    }
  }, []);

  useEffect(() => {
    if (key) {
      checkToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.style.backgroundColor = backgroundCanvas;
      document.documentElement.style.backgroundColor = backgroundCanvas;
      const rootEl = document.getElementById("root");
      if (rootEl) {
        rootEl.style.backgroundColor = backgroundCanvas;
      }
    }
  }, [backgroundCanvas]);

  CheckSession({ navigate });
  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="app" style={{ backgroundColor: backgroundCanvas }}>
          {!hideChrome && (
            <Sidebar onCollapseStateChange={handleSidebarStateChange} />
          )}
          <main
            className="content"
            style={{
              backgroundColor: backgroundCanvas,
              marginLeft: hideChrome ? 0 : `${sidebarOffset}px`,
              width: hideChrome ? "100%" : `calc(100% - ${sidebarOffset}px)`,
              transition: hideChrome
                ? "none"
                : "margin-left 0.5s ease-out, width 0.5s ease-out",
            }}
          >
            {!hideChrome && <NavBar />}
            <UserContext.Provider value={{ key, setKey }}>
              <ThreadContext.Provider value={{ thread, setThread }}>
                <ThreadsProvider>
                  <Suspense fallback={null}>
                    <Routes>
                      <Route exact path="/" element={<Landing />} />
                      {publicRoutes.map(({ path, Component }) => (
                        <Route key={path} path={path} element={<Component />} />
                      ))}
                      <Route element={<PrivateRoutes />}>
                        {privateRoutes.map(({ path, Component }) => (
                          <Route key={path} path={path} element={<Component />} />
                        ))}
                      </Route>
                    </Routes>
                  </Suspense>
                </ThreadsProvider>
              </ThreadContext.Provider>
            </UserContext.Provider>
          </main>
        </div>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
